import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { gameApi } from "@/lib/api";
import { HorseArt } from "@/components/game/HorseArt";
import { HorseSilhouette } from "@/components/game/HorseSilhouette";
import { ArrowLeft, Search, Loader2, Pencil, ShieldCheck, Wrench, Star, Trophy, Save, Crown, BookOpen, Gem, Upload, Trash2, Lock, X, UserPlus, UserMinus, Ban, Check, Mountain } from "lucide-react";
import { toast } from "sonner";
import { CollectorsBook } from "@/components/game/CollectorsBook";
import { PublicProfile, HorseThumb } from "@/components/game/HMAViews";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const hmaImg = (p) => (p ? (p.startsWith("http") ? p : `${BACKEND}${p}`) : null);

const ProfileHmaCard = ({ playerNumber }) => {
  const [d, setD] = useState(undefined);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    setD(undefined);
    gameApi.get(`/game/hma/public/${playerNumber}`).then((r) => setD(r.data)).catch(() => setD(null));
  }, [playerNumber]);
  // Hide entirely when loading, when the player has no HMA, or when their HMA is private (to others).
  if (d === undefined || !d || d.error || d.private) return null;
  return (
    <>
      <div className="mt-4 rounded-2xl border border-emerald-300/50 bg-emerald-500/5 p-3" data-testid="profile-hma-card">
        <p className="mb-1.5 flex items-center gap-1 text-sm font-bold text-primary"><Mountain size={14} className="text-emerald-600" /> Herd Management Area</p>
        {d.cover_image && <img src={hmaImg(d.cover_image)} alt="cover" className="mb-2 h-24 w-full rounded-xl object-cover" data-testid="profile-hma-cover" />}
        <p className="font-display text-lg font-bold text-primary" data-testid="profile-hma-name">{d.name}</p>
        <p className="text-[11px] text-muted-foreground">Pop {d.population}/{d.carrying_capacity} · Gathered {d.gathered} · Makeover horses {d.makeover_produced}</p>
        {d.description && <p className="mt-1 whitespace-pre-wrap text-xs text-foreground/80">{d.description}</p>}
        {d.featured?.length > 0 && (
          <div className="mt-2 flex gap-2 overflow-x-auto pb-1" data-testid="profile-hma-featured">
            {d.featured.map((h) => (
              <div key={h.id} className="w-16 shrink-0 text-center">
                <div className="mx-auto grid h-14 w-14 place-items-center overflow-hidden rounded-lg bg-muted"><HorseThumb h={h} size={52} /></div>
                <p className="mt-0.5 truncate text-[10px] font-bold text-primary">{h.label}</p>
              </div>
            ))}
          </div>
        )}
        <button type="button" data-testid="profile-hma-view" onClick={() => setOpen(true)}
          className="mt-2 text-xs font-bold text-secondary underline-offset-2 hover:underline active:scale-95">View full HMA →</button>
      </div>
      {open && (
        <div className="fixed inset-0 z-[80] overflow-y-auto bg-black/60 p-4" data-testid="profile-hma-modal" onClick={() => setOpen(false)}>
          <div className="mx-auto mt-6 w-full max-w-md rounded-2xl bg-card p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="mb-2 flex justify-end"><button data-testid="profile-hma-modal-close" onClick={() => setOpen(false)} className="rounded-full p-1 text-muted-foreground hover:text-primary"><X size={18} /></button></div>
            <PublicProfile playerNumber={playerNumber} />
          </div>
        </div>
      )}
    </>
  );
};

export const BANNERS = {
  navy_cream: { label: "Navy & Cream", css: "linear-gradient(135deg,#1f2a44,#c9b79c)" },
  bluegrass: { label: "Bluegrass", css: "linear-gradient(135deg,#2e5d3b,#7cb342)" },
  sunset: { label: "Sunset", css: "linear-gradient(135deg,#ff7e5f,#feb47b)" },
  sage: { label: "Sage", css: "linear-gradient(135deg,#6b8e6b,#c8d6b9)" },
  charcoal: { label: "Charcoal", css: "linear-gradient(135deg,#232526,#414345)" },
  rose: { label: "Rose", css: "linear-gradient(135deg,#b76e79,#f3c1c6)" },
  clay: { label: "Clay", css: "linear-gradient(135deg,#8a5a44,#d8a47f)" },
  sky: { label: "Sky", css: "linear-gradient(135deg,#2980b9,#a1c4fd)" },
};
export const FRAMES = {
  none: { label: "None", ring: "" },
  gold: { label: "Gold", ring: "ring-4 ring-amber-400" },
  silver: { label: "Silver", ring: "ring-4 ring-slate-300" },
  bronze: { label: "Bronze", ring: "ring-4 ring-orange-700" },
  rope: { label: "Rope", ring: "ring-4 ring-amber-700" },
  ribbon: { label: "Ribbon", ring: "ring-4 ring-rose-400" },
};

const initialsOf = (name) => (name || "?").trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("");

const Avatar = ({ avatar, avatarHorse, favorite, name, frame = "none", frameImage = null, size = 72 }) => {
  const ring = frameImage ? "" : (FRAMES[frame]?.ring || "");
  const box = { width: size, height: size };
  const type = avatar?.type || "default";
  return (
    <div data-testid="profile-avatar" style={box}
      className={`relative grid shrink-0 place-items-center overflow-hidden rounded-2xl bg-muted ${ring}`}>
      {type === "initials" ? (
        <span className="font-display font-black text-primary" style={{ fontSize: size * 0.34 }}>{initialsOf(name)}</span>
      ) : type === "horse" && avatarHorse ? (
        <HorseArt src={avatarHorse.art_image} color={avatarHorse.color_hex || "#8a8a8a"} size={size - 8} />
      ) : (
        <HorseSilhouette color={favorite?.color_hex || "#8a8a8a"} size={size - 10} />
      )}
      {frameImage && <img src={frameImage} alt="" data-testid="avatar-frame-overlay"
        className="pointer-events-none absolute inset-0 h-full w-full object-contain" />}
    </div>
  );
};

const Badge = ({ testid, icon: Icon, label, className }) => (
  <span data-testid={testid} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-bold ${className}`}>
    <Icon size={12} /> {label}
  </span>
);

const BADGE_STYLES = {
  "Game Admin": { icon: Wrench, className: "bg-amber-100 text-amber-700" },
  "Forum Moderator": { icon: ShieldCheck, className: "bg-emerald-100 text-emerald-700" },
  "Founding Player": { icon: Crown, className: "bg-purple-100 text-purple-700" },
  "Champion": { icon: Trophy, className: "bg-green-100 text-green-700" },
};

export const PlayerProfileView = ({ playerNumber, onBack, onEdit, onOpenProfile }) => {
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showBook, setShowBook] = useState(false);
  const navigate = useNavigate();
  const [showStable, setShowStable] = useState(false);
  const [stableData, setStableData] = useState(null);
  const openStable = () => { setShowStable(true); setStableData(null); gameApi.get(`/game/profile/${playerNumber}/stable`).then((r) => setStableData(r.data)).catch(() => setStableData({ stable: [] })); };
  useEffect(() => {
    setLoading(true);
    gameApi.get(`/game/profile/${playerNumber}`).then((r) => setP(r.data)).catch(() => setP(null)).finally(() => setLoading(false));
  }, [playerNumber]);
  const [rel, setRel] = useState(null);
  const [relBusy, setRelBusy] = useState(false);
  const loadRel = (id) => gameApi.get(`/game/friends/status/${id}`).then((r) => setRel(r.data)).catch(() => setRel(null));
  useEffect(() => {
    if (p && !p.is_self && p.id) loadRel(p.id);
    else setRel(null);
  }, [p?.id, p?.is_self]);
  const relAct = (promise, msg) => {
    setRelBusy(true);
    promise.then(() => { if (msg) toast.success(msg); return loadRel(p.id); })
      .catch((e) => toast.error(e.response?.data?.detail || "Action failed"))
      .finally(() => setRelBusy(false));
  };
  const addFriend = () => relAct(gameApi.post("/game/friends/request", { target_id: p.id }), "Friend request sent!");
  const cancelReq = () => relAct(gameApi.post(`/game/friends/request/${rel.request_id}/cancel`), "Request cancelled");
  const acceptReq = () => relAct(gameApi.post(`/game/friends/request/${rel.request_id}/accept`), "Friend added!");
  const declineReq = () => relAct(gameApi.post(`/game/friends/request/${rel.request_id}/decline`), "Declined");
  const removeFriend = () => { if (window.confirm(`Remove ${p.display_name} from friends?`)) relAct(gameApi.post(`/game/friends/${p.id}/remove`), "Friend removed"); };
  const blockPlayer = () => { if (window.confirm(`Block ${p.display_name}? This removes any friendship.`)) relAct(gameApi.post("/game/friends/block", { target_id: p.id }), "Player blocked"); };
  const unblock = () => relAct(gameApi.post("/game/friends/unblock", { target_id: p.id }), "Unblocked");
  const accent = p?.accent_color || null;
  const boxBg = p?.box_color || (accent ? `${accent}18` : null);
  const wash = accent ? `${accent}0d` : null;
  const Stat = ({ label, value }) => (
    <div className="rounded-xl border p-2.5 text-center" data-testid="profile-stat-box"
      style={{ background: boxBg || undefined, borderColor: accent ? `${accent}33` : undefined }}>
      <p className="text-lg font-extrabold text-primary">{value}</p>
      <p className="text-[11px] text-muted-foreground">{label}</p>
    </div>
  );
  return (
    <div className="pb-10" data-testid="player-profile-view">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card/95 px-4 py-3 backdrop-blur">
        <button data-testid="profile-back" onClick={onBack} className="rounded-full p-1 text-muted-foreground"><ArrowLeft size={20} /></button>
        <h2 className="font-display text-xl font-bold text-primary">Player Profile</h2>
        {p?.is_self && !p?.private && (
          <div className="ml-auto flex items-center gap-2">
            <button data-testid="profile-book-btn" onClick={() => setShowBook(true)}
              className="flex items-center gap-1 rounded-full bg-muted px-3 py-1.5 text-xs font-bold text-primary active:scale-95">
              <BookOpen size={13} /> Collectors Book
            </button>
            <button data-testid="profile-edit-btn" onClick={onEdit}
              className="flex items-center gap-1 rounded-full bg-secondary px-3 py-1.5 text-xs font-bold text-white active:scale-95">
              <Pencil size={13} /> Edit Profile
            </button>
          </div>
        )}
        {p && !p.is_self && p.id && (
          <div className="ml-auto flex items-center gap-1.5" data-testid="profile-rel-actions">
            {relBusy ? <Loader2 className="animate-spin text-muted-foreground" size={18} /> : (<>
              {(!rel || rel.state === "none") && (<>
                <button data-testid="profile-add-friend" onClick={addFriend}
                  className="flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground active:scale-95"><UserPlus size={13} /> Add Friend</button>
                <button data-testid="profile-block" onClick={blockPlayer}
                  className="rounded-full border border-destructive/40 p-1.5 text-destructive active:scale-95"><Ban size={13} /></button>
              </>)}
              {rel?.state === "request_sent" && (
                <button data-testid="profile-cancel-request" onClick={cancelReq}
                  className="rounded-full border border-border px-3 py-1.5 text-xs font-bold text-muted-foreground active:scale-95">Request Sent · Cancel</button>
              )}
              {rel?.state === "request_incoming" && (<>
                <button data-testid="profile-accept" onClick={acceptReq}
                  className="flex items-center gap-1 rounded-full bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground active:scale-95"><Check size={13} /> Accept</button>
                <button data-testid="profile-decline" onClick={declineReq}
                  className="rounded-full border border-border px-3 py-1.5 text-xs font-bold text-muted-foreground active:scale-95">Decline</button>
              </>)}
              {rel?.state === "friends" && (<>
                <span className="rounded-full bg-secondary/15 px-2.5 py-1.5 text-xs font-bold text-secondary" data-testid="profile-friends-badge">Friends</span>
                <button data-testid="profile-remove-friend" onClick={removeFriend}
                  className="rounded-full border border-border p-1.5 text-muted-foreground active:scale-95"><UserMinus size={13} /></button>
                <button data-testid="profile-block" onClick={blockPlayer}
                  className="rounded-full border border-destructive/40 p-1.5 text-destructive active:scale-95"><Ban size={13} /></button>
              </>)}
              {rel?.state === "blocked" && (
                <button data-testid="profile-unblock" onClick={unblock}
                  className="rounded-full border border-border px-3 py-1.5 text-xs font-bold text-primary active:scale-95">Unblock</button>
              )}
              {rel?.state === "blocked_by" && <span className="text-xs text-muted-foreground" data-testid="profile-unavailable">Unavailable</span>}
            </>)}
          </div>
        )}
      </div>
      {showBook && <CollectorsBook onBack={() => setShowBook(false)} />}
      {loading ? <p className="p-10 text-center text-muted-foreground" data-testid="profile-loading">Loading profile…</p>
        : !p ? <p className="p-10 text-center text-sm text-muted-foreground" data-testid="profile-notfound">Player not found.</p>
        : (
          <div style={{ background: wash || undefined }}>
            <div className="h-24 w-full bg-cover bg-center"
              style={p.banner_image ? { backgroundImage: `url(${p.banner_image})` } : { background: BANNERS[p.banner]?.css || BANNERS.navy_cream.css }}
              data-testid="profile-banner" />
            <div className="px-4">
              <div className="-mt-9 flex items-end gap-3">
                <Avatar avatar={p.avatar} avatarHorse={p.avatar_horse} favorite={p.favorite_horse} name={p.display_name} frame={p.frame} size={72} />
                <div className="pb-1">
                  <p className="font-display text-2xl font-bold text-primary" data-testid="profile-name">{p.display_name}</p>
                  <p className="text-sm text-secondary">Player #{p.player_number}{p.stable_name ? ` · ${p.stable_name}` : ""}</p>
                  {p.barn && (
                    <button data-testid="profile-barn-affiliation" onClick={() => navigate(`/game/barn/${p.barn.barn_id}`)}
                      className="mt-0.5 text-left text-xs font-semibold text-secondary active:scale-95">
                      Training Barn: {p.barn.barn_name} <span className="font-black">[{p.barn.barn_tag}]</span>
                      {p.barn.barn_role !== "member" ? ` — ${p.barn.barn_role === "owner" ? "Owner" : "Manager"}` : ""}
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5 rounded-2xl border p-2.5" data-testid="profile-badge-area"
                style={{ background: boxBg || undefined, borderColor: accent ? `${accent}33` : "transparent" }}>
                {(p.badges || []).map((b) => {
                  const s = BADGE_STYLES[b] || { icon: Star, className: "bg-muted text-muted-foreground" };
                  return <Badge key={b} testid={`badge-${b.toLowerCase().replace(/\s+/g, "-")}`} icon={s.icon} label={b} className={s.className} />;
                })}
                {p.favorite_horse && <Badge testid="favorite-badge" icon={Star} label={`Favorite: ${p.favorite_horse.name}`} className="bg-primary/10 text-primary" />}
              </div>

              {p.private ? (
                <p className="mt-6 rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="profile-private">This profile is private.</p>
              ) : (
                <>
                  {p.bio && <p className="mt-3 whitespace-pre-wrap rounded-xl border border-border bg-card p-3 text-sm text-foreground/80" data-testid="profile-bio">{p.bio}</p>}

                  {p.level && (
                    <div className="mt-4 rounded-2xl border border-emerald-300/50 bg-emerald-500/5 p-3" data-testid="profile-level">
                      <div className="flex items-baseline justify-between">
                        <p className="font-display text-lg font-bold text-primary">
                          {p.level.is_max ? "Maximum Level Reached" : `Level ${p.level.level}`}
                        </p>
                        <p className="text-xs font-semibold text-muted-foreground">
                          {p.level.is_max ? `${p.level.lifetime_xp.toLocaleString()} lifetime XP` : `${p.level.xp.toLocaleString()} / ${p.level.xp_required.toLocaleString()} XP`}
                        </p>
                      </div>
                      {!p.level.is_max && (
                        <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-muted" data-testid="profile-xp-bar">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(100, Math.round(100 * p.level.xp / Math.max(1, p.level.xp_required)))}%`, background: p.accent_color || "#10b981" }} />
                        </div>
                      )}
                      <p className="mt-2 text-[11px] text-muted-foreground">
                        {p.level.milestones_unlocked}/{p.level.milestones_total} Piceance–East Douglas reward coats
                        {p.level.next_reward_level ? ` · Next reward: Level ${p.level.next_reward_level}` : " · All rewards unlocked"}
                      </p>
                    </div>
                  )}

                  {p.rarest_coat && (
                    <div className="mt-4 flex items-center gap-3 rounded-2xl border p-3"
                      style={{ borderColor: `${p.rarest_coat.color_hex || "#c8a24a"}66`, background: `${p.rarest_coat.color_hex || "#c8a24a"}12` }}
                      data-testid="profile-rarest-coat">
                      <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-xl" style={{ background: `${p.rarest_coat.color_hex || "#8a8a8a"}1a` }}>
                        <HorseArt src={p.rarest_coat.art_image} color={p.rarest_coat.color_hex || "#8a8a8a"} size={56} />
                      </div>
                      <div className="min-w-0">
                        <p className="flex items-center gap-1 text-xs font-bold text-amber-600"><Gem size={12} /> Rarest Coat</p>
                        <p className="truncate font-display text-lg font-bold text-primary">{p.rarest_coat.display_name}</p>
                        <p className="text-[11px] text-muted-foreground">
                          <span className="font-bold">{p.rarest_coat.rarity}</span>
                          {p.rarest_coat.premium ? " · Premium" : ""}
                          {" · "}
                          {p.rarest_coat.owners <= 1 ? "only this trainer owns it" : `${p.rarest_coat.owners} trainers own it`}
                        </p>
                      </div>
                    </div>
                  )}

                  {p.featured_achievements?.length > 0 && (
                    <div className="mt-4" data-testid="profile-featured">
                      <p className="mb-1 flex items-center gap-1 text-sm font-bold text-primary"><Trophy size={14} className="text-amber-500" /> Featured Achievements</p>
                      <div className="flex flex-wrap gap-2">
                        {p.featured_achievements.map((a) => (
                          <span key={a.id} data-testid={`featured-${a.id}`} className="rounded-xl border border-amber-300/60 bg-amber-50 px-2.5 py-1 text-xs font-bold text-amber-800">{a.name}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="mt-4 grid grid-cols-3 gap-2">
                    <Stat label="Makeovers Finished" value={p.makeovers_completed} />
                    <Stat label="Kept" value={p.kept} />
                    <Stat label="Sold" value={p.sold} />
                    <Stat label="Makeovers Won" value={p.championships} />
                    <Stat label="Discipline Competitions Won" value={p.discipline_wins} />
                    <Stat label="Fan Fav" value={p.fan_favorite_wins} />
                    <Stat label="Top Score" value={p.top_score} />
                    <Stat label="Top Sale" value={`${p.top_sale}🪙`} />
                    <Stat label="Coats" value={`${p.coats_collected}/${p.coats_total}`} />
                    <Stat label="Stable" value={p.stable_size} />
                    <Stat label="Achievements" value={p.achievements?.length || 0} />
                  </div>

                  <ProfileHmaCard playerNumber={p.player_number} />

                  {p.public_stable?.length > 0 && (
                    <div className="mt-4" data-testid="profile-stable-preview">
                      <div className="mb-1 flex items-center justify-between">
                        <p className="text-sm font-bold text-primary">Stable Preview</p>
                        {p.stable_size > 0 && (
                          <button type="button" data-testid="view-full-stable" onClick={openStable}
                            className="text-xs font-bold text-secondary underline-offset-2 hover:underline active:scale-95">View full stable ({p.stable_size})</button>
                        )}
                      </div>
                      <div className="flex gap-2 overflow-x-auto pb-1">
                        {p.public_stable.map((h) => (
                          <button key={h.id} type="button" data-testid={`profile-stable-horse-${h.id}`}
                            onClick={() => navigate(`/game/horse/${p.player_number}/${h.id}`)}
                            className="w-20 shrink-0 rounded-xl border border-border bg-card p-2 text-center transition-colors hover:border-secondary active:scale-95">
                            <div className="relative mx-auto grid h-12 w-12 place-items-center overflow-hidden rounded-lg" style={{ background: `${h.color_hex}1a` }}>{h.background_image && <img src={h.background_image} alt="" className="absolute inset-0 h-full w-full object-cover" />}<HorseArt src={h.art_image} color={h.color_hex} size={40} className="relative" /></div>
                            <p className="mt-1 truncate text-xs font-bold text-primary">{h.name}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      {showStable && (
        <div className="fixed inset-0 z-[80] overflow-y-auto bg-black/60 p-4" data-testid="full-stable-modal" onClick={() => setShowStable(false)}>
          <div className="mx-auto mt-6 w-full max-w-md rounded-2xl bg-card p-4 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <p className="font-display text-lg font-bold text-primary">{stableData?.stable_name || `${p?.display_name}'s Stable`}</p>
              <button data-testid="full-stable-close" onClick={() => setShowStable(false)} className="rounded-full p-1 text-muted-foreground hover:text-primary"><X size={18} /></button>
            </div>
            {!stableData ? (
              <p className="py-8 text-center text-sm text-muted-foreground"><Loader2 className="mr-1 inline animate-spin" size={16} /> Loading stable…</p>
            ) : stableData.stable.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground" data-testid="full-stable-empty">No kept horses to show.</p>
            ) : (
              <div className="mt-3 grid grid-cols-3 gap-2" data-testid="full-stable-grid">
                {stableData.stable.map((h) => (
                  <button key={h.id} type="button" data-testid={`full-stable-horse-${h.id}`}
                    onClick={() => navigate(`/game/horse/${p.player_number}/${h.id}`)}
                    className="rounded-xl border border-border bg-background p-2 text-center transition-colors hover:border-secondary active:scale-95">
                    <div className="relative mx-auto grid h-14 w-14 place-items-center overflow-hidden rounded-lg" style={{ background: `${h.color_hex}1a` }}>{h.background_image && <img src={h.background_image} alt="" className="absolute inset-0 h-full w-full object-cover" />}<HorseArt src={h.art_image} color={h.color_hex} size={48} className="relative" /></div>
                    <p className="mt-1 truncate text-xs font-bold text-primary">{h.name}</p>
                    {h.discipline && <p className="truncate text-[10px] text-secondary">{h.discipline}</p>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const Chip = ({ active, onClick, testid, children }) => (
  <button type="button" data-testid={testid} onClick={onClick}
    className={`rounded-full border px-3 py-1.5 text-xs font-bold active:scale-95 ${active ? "border-secondary bg-secondary text-white" : "border-border bg-card text-primary"}`}>
    {children}
  </button>
);

export const ProfileEditor = ({ player, onBack, onSaved }) => {
  const [displayName, setDisplayName] = useState(player.display_name || "");
  const [stableName, setStableName] = useState(player.stable_name || "");
  const [bio, setBio] = useState(player.bio || "");
  const [avatar, setAvatar] = useState(player.avatar || { type: "default" });
  const [banner, setBanner] = useState(player.banner || "navy_cream");
  const [bannerImage, setBannerImage] = useState(player.banner_image || null);
  const [frame, setFrame] = useState(player.frame || "none");
  const [accent, setAccent] = useState(player.accent_color || null);
  const [box, setBox] = useState(player.box_color || null);
  const [theme, setTheme] = useState(player.theme || null);
  const [options, setOptions] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [featured, setFeatured] = useState(player.featured_achievements || []);
  const [achievements, setAchievements] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => { gameApi.get("/game/achievements").then((r) => setAchievements(r.data.achievements || [])).catch(() => {}); }, []);
  useEffect(() => { gameApi.get("/game/profile/options").then((r) => setOptions(r.data)).catch(() => {}); }, []);

  const frameList = options?.frames || Object.entries(FRAMES).map(([id, f]) => ({ id, label: f.label, image: null, locked: false, owned: true }));
  const frameImg = frameList.find((f) => f.id === frame)?.image || null;

  const uploadBanner = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await gameApi.post("/game/profile/banner-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setBannerImage(data.banner_image); toast.success("Banner photo uploaded!");
    } catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const removeBanner = async () => {
    try { await gameApi.post("/game/profile/banner-remove"); setBannerImage(null); toast.success("Reverted to gradient banner."); }
    catch { toast.error("Could not remove banner"); }
  };
  const applyTheme = (t) => { setTheme(t.id); setBanner(t.banner); setAccent(t.accent); if (t.box) setBox(t.box); if (t.frame) setFrame(t.frame); };

  const keptHorses = useMemo(
    () => (player.alumni || []).filter((a) => a.decision === "keep").map((a) => ({ id: a.id, name: a.name, color_hex: a.color_hex, art_image: a.art_image })),
    [player.alumni]);
  const earned = achievements.filter((a) => a.unlocked);
  const avatarHorse = avatar?.type === "horse" ? keptHorses.find((h) => h.id === avatar.horse_id) : null;

  const toggleFeatured = (id) => setFeatured((f) => f.includes(id) ? f.filter((x) => x !== id) : (f.length >= 3 ? f : [...f, id]));

  const save = async () => {
    if (displayName.trim().length < 3) { toast.error("Display name must be at least 3 characters."); return; }
    if (stableName.trim().length < 3) { toast.error("Stable name must be at least 3 characters."); return; }
    setSaving(true);
    try {
      const { data } = await gameApi.post("/game/profile/update", {
        display_name: displayName, stable_name: stableName, bio,
        avatar, banner, frame, accent_color: accent, box_color: box, theme, featured_achievements: featured,
      });
      toast.success("Profile saved!");
      onSaved(data.player);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save profile"); }
    finally { setSaving(false); }
  };

  return (
    <div className="pb-24" data-testid="profile-editor">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card/95 px-4 py-3 backdrop-blur">
        <button data-testid="editor-back" onClick={onBack} className="rounded-full p-1 text-muted-foreground"><ArrowLeft size={20} /></button>
        <h2 className="font-display text-xl font-bold text-primary">Edit Profile</h2>
      </div>

      <div className="px-4 pt-3">
        {/* Live preview */}
        <div className="overflow-hidden rounded-2xl border border-border" data-testid="editor-preview">
          <div className="h-16 w-full bg-cover bg-center" style={bannerImage ? { backgroundImage: `url(${bannerImage})` } : { background: BANNERS[banner]?.css }} />
          <div className="flex items-end gap-3 px-3 pb-3" style={{ background: box || undefined }}>
            <div className="-mt-6"><Avatar avatar={avatar} avatarHorse={avatarHorse} favorite={keptHorses[0]} name={displayName} frame={frame} frameImage={frameImg} size={56} /></div>
            <div className="pb-1">
              <p className="font-display text-lg font-bold" style={{ color: accent || undefined }}>{displayName || "Your Name"}</p>
              <p className="text-xs text-secondary">{stableName || "Your Stable"}</p>
            </div>
          </div>
        </div>

        <label className="mt-4 block text-sm font-bold text-primary">Display Name</label>
        <input data-testid="editor-display-name" value={displayName} maxLength={24} onChange={(e) => setDisplayName(e.target.value)}
          className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 outline-none focus:ring-2 focus:ring-secondary" />
        <p className="mt-0.5 text-[11px] text-muted-foreground">3–24 characters</p>

        <label className="mt-3 block text-sm font-bold text-primary">Stable Name</label>
        <input data-testid="editor-stable-name" value={stableName} maxLength={30} onChange={(e) => setStableName(e.target.value)}
          className="mt-1 h-11 w-full rounded-xl border border-border bg-background px-3 outline-none focus:ring-2 focus:ring-secondary" />

        <label className="mt-3 block text-sm font-bold text-primary">Bio</label>
        <textarea data-testid="editor-bio" value={bio} maxLength={300} rows={3} onChange={(e) => setBio(e.target.value)}
          className="mt-1 w-full rounded-xl border border-border bg-background px-3 py-2 outline-none focus:ring-2 focus:ring-secondary" placeholder="Tell other trainers about your stable…" />
        <p className="mt-0.5 text-right text-[11px] text-muted-foreground">{bio.length}/300</p>

        <p className="mt-3 text-sm font-bold text-primary">Avatar</p>
        <div className="mt-1 flex flex-wrap gap-2">
          <Chip testid="avatar-default" active={avatar.type === "default"} onClick={() => setAvatar({ type: "default" })}>Default</Chip>
          <Chip testid="avatar-initials" active={avatar.type === "initials"} onClick={() => setAvatar({ type: "initials" })}>Initials</Chip>
          {keptHorses.map((h) => (
            <Chip key={h.id} testid={`avatar-horse-${h.id}`} active={avatar.type === "horse" && avatar.horse_id === h.id}
              onClick={() => setAvatar({ type: "horse", horse_id: h.id })}>🐴 {h.name}</Chip>
          ))}
          {keptHorses.length === 0 && <span className="text-xs text-muted-foreground">Keep a horse to use it as your avatar.</span>}
        </div>

        <p className="mt-3 text-sm font-bold text-primary">Themes <span className="font-normal text-muted-foreground">(one tap sets banner, colors &amp; frame)</span></p>
        <div className="mt-1 flex flex-wrap gap-2">
          {(options?.themes || []).map((t) => (
            <button key={t.id} type="button" data-testid={`theme-${t.id}`} onClick={() => applyTheme(t)}
              className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs font-bold active:scale-95 ${theme === t.id ? "border-secondary bg-secondary text-white" : "border-border bg-card text-primary"}`}>
              <span className="h-3.5 w-3.5 rounded-full" style={{ background: t.accent }} /> {t.label}
            </button>
          ))}
        </div>
        {bannerImage && (
          <p className="mt-1.5 text-[11px] font-semibold text-amber-600" data-testid="banner-override-hint">
            Your uploaded banner photo is shown instead of any theme/banner color. Remove it below to use a gradient.
          </p>
        )}

        <p className="mt-3 text-sm font-bold text-primary">Profile Color <span className="font-normal text-muted-foreground">(tints your whole profile, forum & leaderboard)</span></p>
        <div className="mt-1 flex items-center gap-2">
          <input type="color" data-testid="accent-picker" value={accent || "#5a3e2b"} onChange={(e) => { setAccent(e.target.value); setTheme(null); }}
            className="h-10 w-14 cursor-pointer rounded-lg border border-border bg-background" />
          <span className="text-xs font-semibold text-muted-foreground">{accent || "Default"}</span>
          {accent && <button type="button" data-testid="accent-clear" onClick={() => { setAccent(null); setTheme(null); }} className="text-xs font-bold text-secondary">Reset</button>}
        </div>

        <p className="mt-3 text-sm font-bold text-primary">Box &amp; Card Color <span className="font-normal text-muted-foreground">(tints your stat boxes &amp; badges)</span></p>
        <div className="mt-1 flex items-center gap-2">
          <input type="color" data-testid="box-picker" value={box || "#f1ede8"} onChange={(e) => { setBox(e.target.value); setTheme(null); }}
            className="h-10 w-14 cursor-pointer rounded-lg border border-border bg-background" />
          <span className="text-xs font-semibold text-muted-foreground">{box || "Default"}</span>
          {box && <button type="button" data-testid="box-clear" onClick={() => { setBox(null); setTheme(null); }} className="text-xs font-bold text-secondary">Reset</button>}
        </div>

        <p className="mt-3 text-sm font-bold text-primary">Banner</p>
        {bannerImage ? (
          <div className="mt-1 overflow-hidden rounded-xl border border-border">
            <img src={bannerImage} alt="banner" className="h-20 w-full object-cover" data-testid="editor-banner-image" />
            <button type="button" data-testid="banner-remove" onClick={removeBanner}
              className="flex w-full items-center justify-center gap-1 bg-muted py-1.5 text-xs font-bold text-destructive"><Trash2 size={12} /> Remove photo (use a gradient)</button>
          </div>
        ) : (
          <div className="mt-1 grid grid-cols-4 gap-2">
            {Object.entries(BANNERS).map(([id, b]) => (
              <button key={id} type="button" data-testid={`banner-${id}`} onClick={() => setBanner(id)}
                className={`h-10 rounded-lg ${banner === id ? "ring-2 ring-secondary ring-offset-2" : ""}`} style={{ background: b.css }} title={b.label} />
            ))}
          </div>
        )}
        <label className="mt-2 flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-border py-2.5 text-xs font-bold text-primary active:scale-95" data-testid="banner-upload-label">
          <Upload size={14} /> {uploading ? "Uploading…" : "Upload banner photo"}
          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" data-testid="banner-upload-input" onChange={uploadBanner} disabled={uploading} />
        </label>
        <p className="mt-0.5 text-[11px] text-muted-foreground">Recommended 1200×400px (3:1). Max 8MB.</p>

        <p className="mt-3 text-sm font-bold text-primary">Frame</p>
        <div className="mt-1 flex flex-wrap gap-2">
          {frameList.map((f) => (
            <button key={f.id} type="button" data-testid={`frame-${f.id}`} disabled={!f.owned} onClick={() => f.owned && setFrame(f.id)}
              className={`relative flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-bold active:scale-95 ${frame === f.id ? "border-secondary bg-secondary text-white" : "border-border bg-card text-primary"} ${!f.owned ? "opacity-50" : ""}`}>
              {f.image && <img src={f.image} alt="" className="h-4 w-4 rounded object-contain" />}
              {f.label}{f.locked && <Lock size={11} />}
            </button>
          ))}
        </div>

        <p className="mt-3 text-sm font-bold text-primary">Featured Achievements <span className="font-normal text-muted-foreground">(up to 3)</span></p>
        <div className="mt-1 flex flex-wrap gap-2">
          {earned.length === 0 && <span className="text-xs text-muted-foreground" data-testid="editor-no-achievements">Earn achievements to feature them here.</span>}
          {earned.map((a) => (
            <Chip key={a.id} testid={`feature-${a.id}`} active={featured.includes(a.id)} onClick={() => toggleFeatured(a.id)}>{a.name}</Chip>
          ))}
        </div>
      </div>

      <div className="fixed bottom-0 left-0 z-20 w-full border-t border-border bg-card/95 p-3 backdrop-blur">
        <button data-testid="editor-save" disabled={saving} onClick={save}
          className="mx-auto flex h-12 w-full max-w-md items-center justify-center gap-2 rounded-xl bg-primary font-bold text-primary-foreground disabled:opacity-60 active:scale-95">
          <Save size={16} /> {saving ? "Saving…" : "Save Profile"}
        </button>
      </div>
    </div>
  );
};

export const FindPlayers = ({ onBack, onOpenProfile }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) { setResults([]); setSearched(false); return; }
    setLoading(true);
    const t = setTimeout(() => {
      gameApi.get(`/game/players/search?q=${encodeURIComponent(term)}`)
        .then((r) => setResults(r.data.results || [])).catch(() => setResults([]))
        .finally(() => { setLoading(false); setSearched(true); });
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="pb-10" data-testid="find-players-view">
      <div className="sticky top-0 z-10 flex items-center gap-2 border-b border-border bg-card/95 px-4 py-3 backdrop-blur">
        <button data-testid="find-back" onClick={onBack} className="rounded-full p-1 text-muted-foreground"><ArrowLeft size={20} /></button>
        <h2 className="font-display text-xl font-bold text-primary">Find Players</h2>
      </div>
      <div className="px-4 pt-3">
        <div className="flex items-center gap-2 rounded-xl border border-border bg-background px-3">
          <Search size={16} className="text-muted-foreground" />
          <input data-testid="find-input" autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by name, stable, or #ID"
            className="h-11 w-full bg-transparent outline-none" />
        </div>
        {loading ? (
          <p className="mt-6 flex items-center justify-center gap-2 text-sm text-muted-foreground"><Loader2 className="animate-spin" size={16} /> Searching…</p>
        ) : results.length > 0 ? (
          <div className="mt-3 space-y-2" data-testid="find-results">
            {results.map((e) => (
              <button key={e.player_number} data-testid={`find-row-${e.player_number}`} onClick={() => onOpenProfile(e.player_number)}
                className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left active:scale-[0.99]">
                <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg" style={{ background: `${e.thumbnail_color || "#cccccc"}1a` }}><HorseSilhouette color={e.thumbnail_color || "#8a8a8a"} size={36} /></div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-bold text-primary">{e.display_name} <span className="text-xs font-normal text-muted-foreground">#{e.player_number}</span></p>
                  <p className="truncate text-xs text-secondary">{e.stable_name}{e.favorite_horse ? ` · ${e.favorite_horse}` : ""}</p>
                </div>
                <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[11px] font-bold text-muted-foreground">{e.makeovers} makeovers</span>
              </button>
            ))}
          </div>
        ) : searched ? (
          <p className="mt-8 text-center text-sm text-muted-foreground" data-testid="find-empty">No players found for “{q}”.</p>
        ) : (
          <p className="mt-8 text-center text-sm text-muted-foreground" data-testid="find-hint">Type at least 2 characters to search.</p>
        )}
      </div>
    </div>
  );
};
